import formatPriceToFloat from '../../../utils/price/format-price-to-float';
import qualityEventsHelpers from '../quality-events.helpers';
import serializeQueryParams from '../../../utils/url/serialize-query-params';
import { QUERY_MOBILE } from '../../../constants/query-params/mobile';
import { storageKeys } from '../../../utils/browser-storage/storage-keys';
import { QUERY_MATERIAL } from '../../../constants/query-params/material';

export type CheckoutPageSource = 'generic' | 'paypal' | 'buy_now';

export const CHECKOUT_PAGE_EVENTS = {
    applyCouponCode: (props: {
        couponCode: string;
        discountAmount: number;
        cartValueBefore: number;
        cartValueAfter: number;
    }): Promise<void> =>
        qualityEventsHelpers.track({
            type: 'applyCouponCode',
            extra: getExtra({
                coupon_code: props.couponCode,
                discount_amount: props.discountAmount,
                cart_value_before: props.cartValueBefore,
                cart_value_after: props.cartValueAfter
            })
        }),

    couponRejected: (props: { couponCode: string; rejectionReason: string; cartTotalValue: string }): Promise<void> =>
        qualityEventsHelpers.track({
            type: 'couponRejected',
            extra: getExtra({
                coupon_code: props.couponCode,
                rejection_reason: props.rejectionReason,
                cart_total_value: props.cartTotalValue
            })
        }),

    initiateCheckout: (props: {
        cartTotalValue: string;
        onFinish: VoidFunction;
        cartItems: CartItem[];
        internalPath?: string;
    }): Promise<void> => {
        const source = getCheckoutSource();
        const checkoutValue = formatPriceToFloat(props.cartTotalValue);
        const itemPrice = Math.floor(checkoutValue * 100);

        return qualityEventsHelpers
            .track({
                type: 'initiateCheckout',
                item_price: itemPrice,
                total_results: props.cartItems.length,
                ...(source && { source }),
                ...(props.internalPath && { internal_path: props.internalPath }),
                extra: getExtra({
                    cart_total_value: props.cartTotalValue,
                    cart_item_count: props.cartItems.length,
                    items: props.cartItems?.map((item) => ({ id: item.id, finalPrice: item.finalPrice })) ?? []
                })
            })
            .finally(props.onFinish);
    },

    selectPaymentProvider: (props: {
        cartTotalValue: string;
        paymentProvider: string;
        paymentProviderPosition: number;
    }): Promise<void> => {
        const source = getCheckoutSource();
        const checkoutValue = formatPriceToFloat(props.cartTotalValue);
        const itemPrice = Math.floor(checkoutValue * 100);

        return qualityEventsHelpers.track({
            type: 'selectPaymentProvider',
            item_price: itemPrice,
            ...(source && { source }),
            extra: getExtra({
                payment_provider: props.paymentProvider,
                cart_total_value: props.cartTotalValue,
                checkout_value: checkoutValue,
                position: props.paymentProviderPosition
            })
        });
    },
    startPayment(userId: User['id'], paymentOption: string, cartFinalPrice?: string) {
        const source = getCheckoutSource();
        const itemPrice = cartFinalPrice ? Math.floor(formatPriceToFloat(cartFinalPrice) * 100) : undefined;

        return qualityEventsHelpers.track({
            type: 'startPayment',
            user_id: (userId || '').toString(),
            ...(itemPrice !== undefined && { item_price: itemPrice }),
            ...(source && { source }),
            extra: paymentOption
        });
    },
    abandonedPayment(userId: User['id'], paymentOption: string, cartFinalPrice?: string) {
        const source = getCheckoutSource();
        const itemPrice = cartFinalPrice ? Math.floor(formatPriceToFloat(cartFinalPrice) * 100) : undefined;

        qualityEventsHelpers.track({
            type: 'abandonedPayment',
            user_id: (userId || '').toString(),
            ...(itemPrice !== undefined && { item_price: itemPrice }),
            ...(source && { source }),
            extra: paymentOption
        });
    },
    failedPayment(userId: User['id'], paymentOption: string, cartFinalPrice?: string, errorMessage?: string) {
        const source = getCheckoutSource();
        const itemPrice = cartFinalPrice ? Math.floor(formatPriceToFloat(cartFinalPrice) * 100) : undefined;

        qualityEventsHelpers.track({
            type: 'failedPayment',
            user_id: (userId || '').toString(),
            ...(itemPrice !== undefined && { item_price: itemPrice }),
            ...(source && { source }),
            extra: JSON.stringify({
                paymentOption,
                ...(errorMessage && { errorMessage }),
            })
        });
    },
    otherPaymentMethodsClick() {
        qualityEventsHelpers.track({
            type: 'otherPaymentMethodsClick'
        });
    },
    cartaDelDocenteContinue(): Promise<void> {
        return qualityEventsHelpers.track({
            type: 'cartaDelDocenteContinue'
        });
    },
    agbCheckboxChange(isChecked: boolean): Promise<void> {
        return qualityEventsHelpers.track({
            type: isChecked ? 'agbAccept' : 'agbCancel'
        });
    },
    checkoutEmailValidationError(): Promise<void> {
        return qualityEventsHelpers.track({
            type: 'checkoutEmailValidationError'
        });
    }
};

export const initCheckoutSourceData = ()=> {
    const params = serializeQueryParams();

    if (params[QUERY_MOBILE.SOURCE]) {
        setCheckoutSource(params[QUERY_MOBILE.SOURCE] as CheckoutPageSource);
    }

    // For cases when user visit Checkout page directly via URL
    if (!getCheckoutSource()) {
        const isBuyNowFlow = window.location.href.includes(`?${QUERY_MATERIAL.MATERIAL_ID}=`);

        setCheckoutSource(isBuyNowFlow ? 'buy_now' : 'generic');
    }
};

export const setCheckoutSource = (source: CheckoutPageSource) => {
    window.storage.setItem(storageKeys.general.checkoutSource, source);
};

export const getCheckoutSource = (): CheckoutPageSource | null => {
    return window.storage.getItem(storageKeys.general.checkoutSource) || null;
}

export const resetCheckoutSource = () => {
    return window.storage.removeItem(storageKeys.general.checkoutSource);
}

const getDateAndTime = (): Record<string, string | number> => {
    const now = new Date();

    return {
        date: now.toISOString(),
        time: now.getTime()
    };
};

const getExtra = (
    extraFields: Record<
        string,
        | string
        | number
        | {
              id: number;
              finalPrice: string;
          }[]
    > = {}
): string | null => JSON.stringify({ ...getDateAndTime(), ...extraFields });
